import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import {
  type EnvironmentId,
  type ProjectId,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { useMemo } from "react";
import { isLatestTurnSettled } from "../../session-logic";
import { useStore } from "../../store";
import {
  createProjectSelectorByRef,
  selectThreadTeamByRef,
  type ThreadTeam,
} from "../../storeSelectors";
import { type Thread } from "../../types";
import { buildAgentsRowViewModel } from "../workbar/WorkBarAgentsRow.logic";
import { type ThreadPlanCatalogEntry, createThreadPlanCatalogSelector } from "../ChatView.logic";

function useThreadPlanCatalog(threadIds: readonly ThreadId[]): ThreadPlanCatalogEntry[] {
  return useStore(useMemo(() => createThreadPlanCatalogSelector(threadIds), [threadIds]));
}

export function useChatProjectContext(input: {
  readonly activeThread: Thread | undefined;
  readonly activeThreadRef: ScopedThreadRef | null;
  readonly activeThreadEnvironmentId: EnvironmentId | undefined;
  readonly activeThreadProjectId: ProjectId | undefined;
}): {
  readonly activeThreadTeam: ThreadTeam | undefined;
  readonly showAgentsRow: boolean;
  readonly activeLatestTurn: Thread["latestTurn"] | null;
  readonly threadPlanCatalog: ThreadPlanCatalogEntry[];
  readonly latestTurnSettled: boolean;
  readonly activeProject: ReturnType<ReturnType<typeof createProjectSelectorByRef>>;
} {
  const threadTeamSelector = useMemo(
    () => selectThreadTeamByRef(input.activeThreadRef),
    [input.activeThreadRef],
  );
  const activeThreadTeam = useStore(threadTeamSelector);
  const showAgentsRow = useMemo(
    () => buildAgentsRowViewModel(activeThreadTeam) !== null,
    [activeThreadTeam],
  );
  const activeLatestTurn = input.activeThread?.latestTurn ?? null;
  const threadPlanCatalog = useThreadPlanCatalog(
    useMemo(() => {
      const threadIds: ThreadId[] = [];
      if (input.activeThread?.id) {
        threadIds.push(input.activeThread.id);
      }
      const sourceThreadId = activeLatestTurn?.sourceProposedPlan?.threadId;
      if (sourceThreadId && sourceThreadId !== input.activeThread?.id) {
        threadIds.push(sourceThreadId);
      }
      return threadIds;
    }, [activeLatestTurn?.sourceProposedPlan?.threadId, input.activeThread?.id]),
  );
  const latestTurnSettled = isLatestTurnSettled(
    activeLatestTurn,
    input.activeThread?.session ?? null,
  );
  const activeProjectRef = useMemo(
    () =>
      input.activeThreadEnvironmentId && input.activeThreadProjectId
        ? scopeProjectRef(input.activeThreadEnvironmentId, input.activeThreadProjectId)
        : null,
    [input.activeThreadEnvironmentId, input.activeThreadProjectId],
  );
  const activeProject = useStore(
    useMemo(() => createProjectSelectorByRef(activeProjectRef), [activeProjectRef]),
  );

  return {
    activeThreadTeam,
    showAgentsRow,
    activeLatestTurn,
    threadPlanCatalog,
    latestTurnSettled,
    activeProject,
  };
}
